import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {qualify, pluginName, typeName} from '@packages/systems/wfdl-Qualified';
import {call, call2, _$} from '@packages/utilities/wfdl-helpers';
import {
  FALSE,
  TPredicate,
  TRUE,
  runPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {OptionalText} from './OptionalText';
import {
  EnvTypes,
  Type,
  Expressions,
  Plugins,
  TypeImplementation,
  ExpressionPath,
  withSystem,
} from '@packages/systems/wfdl';
import {
  ECall,
  EHole,
  EText,
  Expression,
  functionOf,
  TList,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {
  CaseContains,
  CaseDoesNotContain,
  CaseDoesNotEndWith,
  CaseDoesNotEqual,
  CaseDoesNotStartWith,
  CaseEndsWith,
  CaseEquals,
  CaseStartsWith,
} from './predicates';
import {CaseSensitiveTextType} from './CaseSensitiveText';
export {TCaseSensitiveText} from './CaseSensitiveText';

const TEXT_PLUGIN_VALUES_CREATORS = {
  // Do not use in new code, use Text::CaseEquals instead
  // TODO: Remove this with Logic code
  Equals: (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(inputType, TPredicate(inputType)),
    },
    value: (text1: string) => (text2: string) => toBooleanTag(text1 === text2),
  }),

  // Do not use in new code, use Text::CaseDoesNotEqual instead
  // TODO: Remove this with Logic code
  'Does not equal': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(inputType, TPredicate(inputType)),
    },
    value: (text1: string) => (text2: string) => toBooleanTag(text1 !== text2),
  }),

  // Do not use in new code, use Text::CaseContains instead
  // TODO: Remove this with Logic code
  Contains: (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (search: string) => (textToSearch: string) =>
      toBooleanTag(textToSearch.indexOf(search) !== -1),
  }),

  // Do not use in new code, use Text::CaseDoesNotContain instead
  // TODO: Remove this with Logic code
  'Does not contain': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (search: string) => (textToSearch: string) =>
      toBooleanTag(textToSearch.indexOf(search) === -1),
  }),

  // Do not use in new code, use Text::CaseStartsWith instead
  // TODO: Remove this with Logic code
  'Starts with': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (target: string) => (string: string) =>
      toBooleanTag(string.slice(0, target.length) === target),
  }),

  // Do not use in new code, use Text::CaseDoesNotStartWith instead
  // TODO: Remove this with Logic code
  'Does not start with': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (target: string) => (string: string) =>
      toBooleanTag(string.slice(0, target.length) !== target),
  }),

  // Do not use in new code, use Text::CaseEndsWith instead
  // TODO: Remove this with Logic code
  'Ends with': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (target: string) => (string: string) =>
      target.length > string.length
        ? FALSE
        : toBooleanTag(string.slice(string.length - target.length) === target),
  }),

  // Do not use in new code, use Text::CaseDoesNotEndWith instead
  // TODO: Remove this with Logic code
  'Does not end with': (inputType: Type): EnvTypes.TypedValue => ({
    envType: {
      exported: 'hidden',
      dataType: functionOf(TText, TPredicate(inputType)),
    },
    value: (target: string) => (string: string) =>
      target.length > string.length
        ? TRUE
        : toBooleanTag(string.slice(string.length - target.length) !== target),
  }),
} as const;

export const textEqualsRef = _$('Text', 'Equals');
export const textEquals = (s1: Expression, s2: Expression): Expression =>
  call(runPredicate(call(textEqualsRef, [s1])), [s2]);

export const isEmptyRef = _$('Text', 'Is empty');

export const isEmpty = (str: Expression): Expression =>
  call(runPredicate(isEmptyRef), [str]);

export function createSharedTextPluginValues(
  inputType: Type,
  pluginValuesToShare: Array<keyof typeof TEXT_PLUGIN_VALUES_CREATORS>
) {
  return pluginValuesToShare.reduce<Record<string, any>>(
    (result, valueName) => {
      result[valueName] = TEXT_PLUGIN_VALUES_CREATORS[valueName](inputType);
      return result;
    },
    {}
  );
}

const holeAction = {
  type: 'EXPRESSION_ACTION',
  key: [] as ExpressionPath,
  payload: {newExpression: EHole()},
} as const;

const interceptEmptyText = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    (action: TypeImplementation.ExpressionEditorAction) => {
      if (
        action.type === 'EXPRESSION_ACTION' &&
        Expressions.getText(action.payload.newExpression) === ''
      ) {
        dispatch(holeAction);
        return;
      }
      dispatch(action);
    }
);

export const TNonEmptyText = TTypeConstructor(
  qualify(pluginName('Text'), typeName('Non-empty Text'))
);

export const TOptionalText = TTypeConstructor(
  qualify(pluginName('Text'), typeName('Optional Text'))
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    'Non-empty Text': TypeImplementation.Alias({
      type: TText,
      render: ({expr, dispatch}) => {
        return (
          <ExpressionEditor
            expression={expr}
            dispatch={interceptEmptyText(dispatch)}
          />
        );
      },
      system: {bindUIVariant: 'custom'},
    }),
    'Optional Text': OptionalText,
    CaseSensitiveText: CaseSensitiveTextType,
  },
  values: {
    'Is set': {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is set'}, TPredicate(TText)),
      },
      value: (inputText: string) => toBooleanTag(inputText !== ''),
    },

    'Is not set': {
      envType: {
        exported: 'visible',
        dataType: withSystem({label: 'is not set'}, TPredicate(TText)),
      },
      value: (inputText: string) => toBooleanTag(inputText === ''),
    },

    /* Text predicates for Logic (TODO: Remove with Logic code) */
    ...createSharedTextPluginValues(withSystem({isBindable: true}, TText), [
      'Equals',
      'Does not equal',
      'Contains',
      'Does not contain',
      'Starts with',
      'Does not start with',
      'Ends with',
      'Does not end with',
    ]),

    /* Text predicates with case sensitivity option */
    CaseEquals,
    CaseDoesNotEqual,
    CaseContains,
    CaseDoesNotContain,
    CaseStartsWith,
    CaseDoesNotStartWith,
    CaseEndsWith,
    CaseDoesNotEndWith,

    'Is empty': {
      envType: {
        exported: 'hidden',
        dataType: TPredicate(TText),
      },
      expression: ECall({fun: textEqualsRef, arg: EText('')}),
    },

    Condition: operand(TText),

    Uppercase: {
      envType: {
        exported: 'hidden',
        dataType: functionOf(TText, TText),
      },
      value: (text: string) => text.toUpperCase(),
    },

    Lowercase: {
      envType: {
        exported: 'hidden',
        dataType: functionOf(TText, TText),
      },
      value: (text: string) => text.toLowerCase(),
    },

    Split: {
      envType: {
        exported: 'hidden',
        dataType: functionOf(TText, functionOf(TText, TList(TText))),
      },
      value: (splitter: string) => (text: string) => text.split(splitter),
    },

    Replace: {
      envType: {
        exported: 'hidden',
        dataType: functionOf(
          TText,
          functionOf(TText, functionOf(TText, TText))
        ),
      },
      value:
        (searchText: string) => (replaceText: string) => (fullText: string) =>
          fullText.replace(searchText, replaceText),
    },

    Append: {
      envType: {
        dataType: functionOf(TText, functionOf(TText, TText)),
        exported: 'hidden',
      },
      value: (str1: string) => (str2: string) => str1 + str2,
    },

    Concat: {
      envType: {
        dataType: functionOf(TList(TText), TText),
        exported: 'hidden',
      },
      expression: call(_$('List', 'Fold Right'), [
        _$('Text', 'Append'),
        EText(''),
      ]),
    },
  },
});

export const append = call2(_$('Text', 'Append'));

export const concat: (arg1: Array<Expression>) => Expression = (texts) => {
  if (texts.length < 1) return EText('');
  const text = texts[0];
  if (!text) return EText('');
  if (texts.length === 1) {
    return text;
  }
  return texts.slice(1).reduce(append, text);
};
